import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
  Button,
  Stack,
  Alert
} from "@mui/material";
import { School as SchoolIcon, ArrowForward as ArrowForwardIcon ,SchoolOutlined as SchoolOutlinedIcon} from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function DashboardTrainingCard({ employeeId }) {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardTrainings = async () => {
      if (!employeeId) {
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("No authentication token found");
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `${API_URL}/api/trainings/employee/id/${employeeId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            },
            withCredentials: true
          }
        );

        // Filter for active trainings (Planned or In Progress)
        const activeTrainings = response.data.filter(
          (t) => t.status === "In Progress" || t.status === "Planned"
        );
        setTrainings(activeTrainings);
      } catch (err) {
        //console.error("Error fetching dashboard trainings:", err);
        setError("Failed to load training details");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardTrainings();
  }, [employeeId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, minHeight: "200px" }}>
        <CircularProgress size={40} sx={{ color: "#10b981" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SchoolIcon sx={{ color: "#10b981" }} />
          <Typography variant="subtitle1" fontWeight="600" color="#1a1a1a">
            Assigned Trainings
          </Typography>
        </Stack>
        {trainings.length > 0 && (
          <Button
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate("/updatetraining")}
            sx={{
              color: "#10b981",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "rgba(16, 185, 129, 0.08)" }
            }}
          >
            View All
          </Button>
        )}
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      ) : trainings.length === 0 ? (
        <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flexGrow: 1,
          py: 4,
          textAlign: "center"
        }}
      >
        <SchoolOutlinedIcon
          sx={{
            fontSize: 60,
            color: "success.main",
            mb: 1
          }}
        />
      
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontWeight: 500, mb: 1 }}
        >
          No Pending Trainings!
        </Typography>
      </Box>
      ) : (
        <List sx={{ 
          width: "100%", 
          bgcolor: "background.paper", 
          p: 0,
        }}>
          {trainings.slice(0, 1).map((training) => (
            <ListItem
              key={training.id}
              alignItems="flex-start"
              sx={{
                flexDirection: "column",
                px: 0,
                py: 0.5,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%", mb: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{
                    color: "#111827",
                    fontSize: "1.05rem",
                    cursor: "pointer",
                    "&:hover": { color: "#10b981" }
                  }}
                  onClick={() => navigate("/updatetraining")}
                >
                  {training.title}
                </Typography>
                <Chip
                  label={training.status}
                  size="small"
                  sx={{
                    bgcolor: training.status === "In Progress" ? "#dbeafe" : "#fef3c7",
                    color: training.status === "In Progress" ? "#1e40af" : "#92400e",
                    fontWeight: "600",
                    fontSize: "0.75rem"
                  }}
                />
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Trainer: {training.trainer} &bull; Category: {training.skillCategory}
              </Typography>

              <Box sx={{ width: "100%", mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Progress
                  </Typography>
                  <Typography variant="caption" fontWeight="bold" color="text.primary">
                    {training.progressPercentage || 0}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={training.progressPercentage || 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#e5e7eb",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "#10b981",
                      borderRadius: 4
                    }
                  }}
                />
              </Box>

              <Button
                variant="outlined"
                onClick={() => navigate("/updatetraining")}
                sx={{
                  mt: 1,
                  alignSelf: "stretch",
                 
                  color: "#10b981",
                  textTransform: "none",
                  fontWeight: "500",
                  borderRadius: "8px",
                 
                }}
              >
                Go to Course
              </Button>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
